import { Fragment } from "react"
import { get, map, some, replace, includes, keys, isEmpty, chain } from "lodash"
import { makeCustomFormatter } from "helpers/formatters/numbers"
import { FORMATS } from "helpers/formatters/numbers"

export const DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES = {
  predictedFactorSensitivity: "predicted",
  historicalFactorSensitivity: "historical",
}
export const NOTHELD_CONTRIBUTOR = { id: "NOTHELD", ticker: "NOT HELD", name: "Not Held", disabled: true }
export const numberFormatter = makeCustomFormatter(FORMATS.NUMBER_FORMAT, { emptyValueFiller: "-" })
export const percentageFormatter = value => !Number.isFinite(value) ? "-" : numberFormatter(value * 100)

export function columnMapWithGroupSeparators(array, iteratee) {
  return map(array, (column, columnIndex) => {
    const group = get(column, "group")
    return <Fragment key={`${group}-${columnIndex}`}>
      {group !== get(array, [columnIndex - 1, "group"]) && <th className="separator" />}
      {iteratee(column, columnIndex)}
    </Fragment>
  })
}

export function getContributorNotHeld(original, rebalanced) {
  const rebalancedNotHeld = get(rebalanced, "isNotHeld")
  const originalNotHeld = get(original, "isNotHeld")
  return (rebalancedNotHeld && originalNotHeld) || originalNotHeld
}

/**
 * Note: This function works as long as you cannot add new Unmodeled securities (which is the case in simulator)
 * If we want to allow adding unmodeled securities, we will need to also consider assetClass to avoid possible false equivalencies
 */
export function isMatchingContributor(universalId, contributor) {
  universalId = { ...universalId, id: universalId.id || universalId.identifier }
  contributor = { ...contributor, id: contributor.id || contributor.identifier }

  return some([
    universalId.internalId && universalId.internalId === contributor.internalId,
    universalId.modelProviderId && universalId.modelProviderId === contributor.modelProviderId,
    universalId.bloombergTicker && universalId.bloombergTicker === contributor.bloombergTicker,
    universalId.figi && universalId.figi === contributor.figi,
    universalId.ticker && universalId.ticker === contributor.ticker && universalId.mic === contributor.mic,
    universalId.isin && universalId.isin === contributor.id && contributor.assetClass === "fixedIncome",
    (universalId.indexId ?? universalId.id) === contributor.id && contributor.assetClass === "index",
    universalId.ticker && universalId.ticker === contributor.id, // ticker is the catchall for universalId and id is the catchall for contributor,
  ], (value) => value)
}

export function isMatchingContributorOrSubContributor(universalId, contributor) {
  // Grouped contributors have sub-contributors
  if (!isEmpty(contributor.contributors)) {
    return chain(contributor.contributors)
      .values()
      .some(contributor => isMatchingContributor(universalId, contributor))
      .value()
  }
  return isMatchingContributor(universalId, contributor)
}

export function pruneValue(value, allowDollarUnits = false) {
  if (allowDollarUnits) {
    return replace(value, /[^\d-.kKmMbB]/gi, "")
  }
  // Remove everything that is not a digit, a period or a minus sign
  return replace(value, /[^\d-.]/gi, "")
}

export function getDecimals(value, minimum = 2, maximum = 8) {
  return Math.min(Math.max(/\d*$/.exec(value)[0].length, minimum), maximum)
}

export function requiresRiskFactors(decompositionType) {
  return includes(["risk", "exposure", ...keys(DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES)], decompositionType)
}
